import React, { useEffect, useState, Component } from 'react';
import { FileText, DollarSign, Calendar, Mail, User, Edit2, Trash2, Download, Eye, X, Plus, Check, AlertCircle, Link, ExternalLink, Copy, Loader2, CheckCircle2, MapPin } from 'lucide-react';
import { callWFAPI } from '../utils/helper';
import { InternalNotesButton } from './InternalNotesButton';
export function InvoiceManager({
  rentals,
  invoices: propInvoices,
  onInvoiceDeleted,
  onNotesUpdated
}) {
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [filter, setFilter] = useState('future');
  const [copiedInvoiceId, setCopiedInvoiceId] = useState(null);
  const [applyingPayment, setApplyingPayment] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const [successPaymentDetails, setSuccessPaymentDetails] = useState(null);
  const [deleteClickCount, setDeleteClickCount] = useState({});
  const [deleteClickTimer, setDeleteClickTimer] = useState(null);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  useEffect(() => {
    if (propInvoices && propInvoices.length > 0) {
      // Add unique IDs to line items if they don't have them
      const invoicesWithIds = propInvoices.map(invoice => ({
        ...invoice,
        lineItems: invoice.lineItems.map((item, index) => ({
          ...item,
          id: item.id || `${invoice.id}-item-${index}`
        }))
      }));
      setInvoices(invoicesWithIds);
    }
  }, [propInvoices]);
  const loadInvoices = () => {
    const stored = localStorage.getItem('invoices');
    if (stored) {
      try {
        setInvoices(JSON.parse(stored));
      } catch (error) {
        console.error('Error loading invoices:', error);
      }
    }
  };
  const saveInvoices = updatedInvoices => {
    localStorage.setItem('invoices', JSON.stringify(updatedInvoices));
    setInvoices(updatedInvoices);
  };
  const generatePaymentLink = invoice => {
    return `${window.location.origin}/pay/${invoice.id}`;
  };
  const copyLinkToClipboard = (invoice, e) => {
    if (e) {
      e.stopPropagation();
    }
    const link = generatePaymentLink(invoice);
    navigator.clipboard.writeText(link).then(() => {
      setCopiedInvoiceId(invoice.id);
      setTimeout(() => setCopiedInvoiceId(null), 2000);
    });
  };
  const openInvoiceLink = (invoice, e) => {
    if (e) {
      e.stopPropagation();
    }
    const link = generatePaymentLink(invoice);
    window.open(link, '_blank');
  };
  const startEditing = invoice => {
    // Only allow editing if not fully paid
    if (invoice.balance > 0) {
      // Get the rental to fetch address information
      const rental = rentals.find(r => r.invoiceId === invoice.id);
      setEditingInvoice({
        ...invoice,
        address: rental?.customerInfo?.address || '',
        address2: rental?.customerInfo?.address2 || '',
        city: rental?.customerInfo?.city || '',
        state: rental?.customerInfo?.state || '',
        zip: rental?.customerInfo?.zip || ''
      });
    } else {
      alert('Cannot edit a fully paid invoice');
    }
  };
  const saveEdit = async () => {
    if (!editingInvoice) return;
    try {
      // Prepare line items for database
      const lineItems = editingInvoice.lineItems.map(item => ({
        Title: item.description,
        Quantity: item.quantity,
        Price: item.price
      }));
      // Update invoice in database
      const data = {
        appname: 'tkd-score',
        reportname: 'All_Rental_Invoices',
        id: editingInvoice.id,
        params: JSON.stringify({
          Customer_Name: editingInvoice.customerName,
          Customer_Email: editingInvoice.customerEmail,
          Shipping_Address: {
            address_line_1: editingInvoice.address || '',
            address_line_2: editingInvoice.address2 || '',
            district_city: editingInvoice.city || '',
            state_province: editingInvoice.state || '',
            postal_code: editingInvoice.zip || ''
          },
          Subtotal: editingInvoice.subtotal,
          California_Tax: editingInvoice.tax,
          Discount_Amount: editingInvoice.discount,
          Total_Amount: editingInvoice.total,
          Paid_Amount: editingInvoice.amountPaid,
          Items: lineItems
        })
      };
      await callWFAPI(0, 'zohoCreator', data);
      // Update local state
      const updatedInvoices = invoices.map(inv => inv.id === editingInvoice.id ? editingInvoice : inv);
      setInvoices(updatedInvoices);
      setEditingInvoice(null);
      setSelectedInvoice(editingInvoice);
    } catch (error) {
      console.error('Error saving invoice:', error);
      alert('Failed to save invoice. Please try again.');
    }
  };
  const cancelEdit = () => {
    setEditingInvoice(null);
  };
  const updateLineItem = (itemId, field, value) => {
    const updatedLineItems = editingInvoice.lineItems.map(item => {
      if (item.id === itemId) {
        const updated = {
          ...item,
          [field]: value
        };
        if (field === 'quantity' || field === 'price') {
          updated.total = updated.quantity * updated.price;
        }
        return updated;
      }
      return item;
    });
    const subtotal = updatedLineItems.reduce((sum, item) => sum + item.total, 0);
    const total = subtotal - editingInvoice.discount + editingInvoice.tax;
    const balance = total - editingInvoice.amountPaid;
    setEditingInvoice({
      ...editingInvoice,
      lineItems: updatedLineItems,
      subtotal,
      total,
      balance
    });
  };
  const addLineItem = () => {
    const newItem = {
      id: Date.now().toString(),
      description: 'New Item',
      quantity: 1,
      price: 0,
      total: 0
    };
    const updatedLineItems = [...editingInvoice.lineItems, newItem];
    const subtotal = updatedLineItems.reduce((sum, item) => sum + item.total, 0);
    const total = subtotal - editingInvoice.discount + editingInvoice.tax;
    const balance = total - editingInvoice.amountPaid;
    setEditingInvoice({
      ...editingInvoice,
      lineItems: updatedLineItems,
      subtotal,
      total,
      balance
    });
  };
  const removeLineItem = itemId => {
    const updatedLineItems = editingInvoice.lineItems.filter(item => item.id !== itemId);
    const subtotal = updatedLineItems.reduce((sum, item) => sum + item.total, 0);
    const total = subtotal - editingInvoice.discount + editingInvoice.tax;
    const balance = total - editingInvoice.amountPaid;
    setEditingInvoice({
      ...editingInvoice,
      lineItems: updatedLineItems,
      subtotal,
      total,
      balance
    });
  };
  const updateInvoiceField = (field, value) => {
    const updated = {
      ...editingInvoice,
      [field]: value
    };
    if (field === 'discount' || field === 'tax' || field === 'amountPaid') {
      const total = updated.subtotal - updated.discount + updated.tax;
      updated.total = total;
      updated.balance = total - updated.amountPaid;
    }
    setEditingInvoice(updated);
  };
  const deleteInvoice = async invoiceId => {
    const invoice = invoices.find(inv => inv.id === invoiceId);
    // Check if invoice is paid (non-zero balance)
    if (invoice && invoice.balance === 0 && invoice.total > 0) {
      alert('Cannot delete a fully paid invoice.');
      return;
    }
    if (window.confirm('Are you sure you want to delete this invoice? This action cannot be undone.')) {
      try {
        const data = {
          appname: 'tkd-score',
          reportname: 'All_Rental_Invoices',
          id: invoiceId,
          delete: 1
        };
        await callWFAPI(0, 'zohoCreator', data);
        const updatedInvoices = invoices.filter(inv => inv.id !== invoiceId);
        setInvoices(updatedInvoices);
        setSelectedInvoice(null);
        // Notify parent component to reload data
        if (onInvoiceDeleted) {
          onInvoiceDeleted();
        }
      } catch (error) {
        console.error('Error deleting invoice:', error);
        alert('Failed to delete invoice. Please try again.');
      }
    }
  };
  const handleDeleteClick = (invoiceId, e) => {
    if (e) {
      e.stopPropagation();
    }
    const invoice = invoices.find(inv => inv.id === invoiceId);
    // Special handling for zero-dollar invoices - require triple click to enable
    if (invoice && invoice.total === 0) {
      const currentCount = deleteClickCount[invoiceId] || 0;
      const newCount = currentCount + 1;
      // Clear existing timer
      if (deleteClickTimer) {
        clearTimeout(deleteClickTimer);
      }
      // Update click count using functional update to ensure we have latest state
      setDeleteClickCount(prev => ({
        ...prev,
        [invoiceId]: newCount
      }));
      // Reset count after 2 seconds
      const timer = setTimeout(() => {
        setDeleteClickCount(prev => ({
          ...prev,
          [invoiceId]: 0
        }));
      }, 2000);
      setDeleteClickTimer(timer);
      // If reached 3 clicks, enable deletion
      if (newCount >= 3) {
        // Clear the timer since we're deleting
        clearTimeout(timer);
        deleteInvoice(invoiceId);
        setDeleteClickCount(prev => ({
          ...prev,
          [invoiceId]: 0
        }));
      }
      return;
    }
    // For non-zero invoices, delete normally
    deleteInvoice(invoiceId);
  };
  const getStatusColor = invoice => {
    if (invoice.balance === 0) {
      return 'bg-green-100 text-green-800 border-green-200';
    } else if (invoice.amountPaid > 0) {
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    } else if (invoice.status === 'Draft') {
      return 'bg-slate-100 text-slate-800 border-slate-200';
    } else {
      return 'bg-blue-100 text-blue-800 border-blue-200';
    }
  };
  const getStatusText = invoice => {
    if (invoice.balance === 0) return 'Paid in Full';
    if (invoice.amountPaid > 0) return 'Partially Paid';
    if (invoice.status === 'Draft') return 'Draft';
    return 'Unpaid';
  };
  const getRingCount = invoice => {
    const ringItem = invoice.lineItems.find(item => item.description.toLowerCase().includes('ring'));
    return ringItem ? ringItem.quantity : 0;
  };
  const getAssignedTechnician = invoice => {
    // Find the rental that matches this invoice
    const rental = rentals.find(r => r.invoiceId === invoice.id);
    return rental?.assignedTechnician || null;
  };
  const getRentalForInvoice = invoice => {
    // Find the rental that matches this invoice
    return rentals.find(r => r.invoiceId === invoice.id);
  };
  const filteredInvoices = invoices.filter(invoice => {
    // Apply time-based filter first
    if (filter === 'future' || filter === 'past') {
      const rental = getRentalForInvoice(invoice);
      if (!rental?.proposedEventDate) return filter === 'past'; // No date = past
      const eventDate = new Date(rental.proposedEventDate + 'T00:00:00');
      if (filter === 'future') {
        return eventDate >= today;
      } else {
        return eventDate < today;
      }
    }
    if (filter === 'all') return true;
    const status = getStatusText(invoice);
    // Map filter values to status text
    if (filter === 'unpaid') {
      return status === 'Unpaid';
    } else if (filter === 'partially-paid') {
      return status === 'Partially Paid';
    } else if (filter === 'paid-in-full') {
      return status === 'Paid in Full';
    }
    return false;
  }).sort((a, b) => {
    // Sort by event date (soonest first), invoices with no date go to the end
    const rentalA = getRentalForInvoice(a);
    const rentalB = getRentalForInvoice(b);
    const dateA = rentalA?.proposedEventDate ? new Date(rentalA.proposedEventDate + 'T00:00:00').getTime() : Infinity;
    const dateB = rentalB?.proposedEventDate ? new Date(rentalB.proposedEventDate + 'T00:00:00').getTime() : Infinity;
    return dateA - dateB;
  });
  const startApplyingPayment = (invoice, e) => {
    if (e) {
      e.stopPropagation();
    }
    setApplyingPayment(invoice);
    setPaymentAmount(invoice.balance.toFixed(2));
    setPaymentMethod('cash');
    setPaymentNotes('');
  };
  const cancelApplyingPayment = () => {
    setApplyingPayment(null);
    setPaymentAmount('');
    setPaymentMethod('cash');
    setPaymentNotes('');
  };
  const submitPayment = async () => {
    if (!applyingPayment || !paymentAmount) {
      alert('Please enter a payment amount');
      return;
    }
    const amount = parseFloat(paymentAmount);
    if (isNaN(amount) || amount <= 0) {
      alert('Please enter a valid payment amount');
      return;
    }
    if (amount > applyingPayment.balance) {
      alert('Payment amount cannot exceed the balance due');
      return;
    }
    setIsProcessingPayment(true);
    try {
      const newAmountPaid = applyingPayment.amountPaid + amount;
      const newBalance = applyingPayment.total - newAmountPaid;
      // Create payment record in database
      const paymentParams = {
        Amount: amount.toString(),
        Description: applyingPayment.invoiceNumber,
        Notes: paymentNotes || '',
        Type_field: paymentMethod === 'cash' ? 'Cash' : 'Check'
      };
      const params = {
        appname: 'tkd-score',
        formname: 'Rental_Payment',
        params: JSON.stringify(paymentParams)
      };
      await callWFAPI(0, 'zohoCreator', params);
      // Update invoice in database
      const data = {
        appname: 'tkd-score',
        reportname: 'All_Rental_Invoices',
        id: applyingPayment.id,
        params: JSON.stringify({
          Paid_Amount: newAmountPaid
        })
      };
      await callWFAPI(0, 'zohoCreator', data);
      // Update local state
      const updatedInvoices = invoices.map(inv => inv.id === applyingPayment.id ? {
        ...inv,
        amountPaid: newAmountPaid,
        balance: newBalance
      } : inv);
      setInvoices(updatedInvoices);
      // Update selected invoice if it's the one being paid
      if (selectedInvoice?.id === applyingPayment.id) {
        setSelectedInvoice({
          ...selectedInvoice,
          amountPaid: newAmountPaid,
          balance: newBalance
        });
      }
      // Store success details and show success popup
      setSuccessPaymentDetails({
        amount,
        method: paymentMethod,
        notes: paymentNotes,
        invoiceNumber: applyingPayment.invoiceNumber,
        customerName: applyingPayment.customerName,
        newBalance
      });
      cancelApplyingPayment();
      setShowPaymentSuccess(true);
    } catch (error) {
      console.error('Error applying payment:', error);
      alert('Failed to apply payment. Please try again.');
    } finally {
      setIsProcessingPayment(false);
    }
  };
  return <div data-id="element-818">
      <div className="mb-6" data-id="element-819">
        <h2 className="text-lg sm:text-xl font-semibold text-slate-800 mb-2" data-id="element-820">
          Invoice Management
        </h2>
        <p className="text-sm text-slate-600" data-id="element-821">
          View, edit, and manage customer invoices
        </p>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2" data-id="element-822">
        <button onClick={() => setFilter('future')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${filter === 'future' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`} data-id="element-823">
          Future Events (
          {invoices.filter(inv => {
          const rental = getRentalForInvoice(inv);
          if (!rental?.proposedEventDate) return false;
          const eventDate = new Date(rental.proposedEventDate + 'T00:00:00');
          return eventDate >= today;
        }).length}
          )
        </button>
        <button onClick={() => setFilter('past')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${filter === 'past' ? 'bg-slate-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`} data-id="element-824">
          Past Events (
          {invoices.filter(inv => {
          const rental = getRentalForInvoice(inv);
          if (!rental?.proposedEventDate) return true;
          const eventDate = new Date(rental.proposedEventDate + 'T00:00:00');
          return eventDate < today;
        }).length}
          )
        </button>
        <button onClick={() => setFilter('all')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${filter === 'all' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`} data-id="element-825">
          All ({invoices.length})
        </button>
        <button onClick={() => setFilter('unpaid')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${filter === 'unpaid' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`} data-id="element-826">
          Unpaid (
          {invoices.filter(inv => getStatusText(inv) === 'Unpaid').length})
        </button>
        <button onClick={() => setFilter('partially-paid')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${filter === 'partially-paid' ? 'bg-yellow-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`} data-id="element-827">
          Partially Paid (
          {invoices.filter(inv => getStatusText(inv) === 'Partially Paid').length}
          )
        </button>
        <button onClick={() => setFilter('paid-in-full')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${filter === 'paid-in-full' ? 'bg-green-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`} data-id="element-828">
          Paid in Full (
          {invoices.filter(inv => getStatusText(inv) === 'Paid in Full').length}
          )
        </button>
      </div>

      {/* Invoice List */}
      {filteredInvoices.length === 0 ? <div className="text-center py-12" data-id="element-829">
          <FileText size={48} className="text-slate-300 mx-auto mb-4" data-id="element-830" />
          <p className="text-slate-600" data-id="element-831">No invoices found</p>
          <p className="text-sm text-slate-500 mt-1" data-id="element-832">
            {filter !== 'all' ? 'Try changing the filter' : 'Create invoices from rentals'}
          </p>
        </div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-833">
          {filteredInvoices.map(invoice => <div key={invoice.id} className="bg-white border border-slate-200 rounded-lg p-4 hover:shadow-lg transition-shadow" data-id="element-834">
              <div className="cursor-pointer" onClick={() => setSelectedInvoice(invoice)} data-id="element-835">
                <div className="flex items-start justify-between mb-3" data-id="element-836">
                  <div data-id="element-837">
                    <h4 className="font-semibold text-slate-800" data-id="element-838">
                      {invoice.invoiceNumber}
                    </h4>
                    <p className="text-sm text-slate-500" data-id="element-839">
                      {invoice.customerName}
                    </p>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-840">
                    {getRentalForInvoice(invoice) && <InternalNotesButton rentalId={getRentalForInvoice(invoice).id} initialNotes={getRentalForInvoice(invoice).internalNotes} onNotesSaved={onNotesUpdated} data-id="element-841" />}
                    <span className={`px-2 py-1 rounded text-xs font-medium border ${getStatusColor(invoice)}`} data-id="element-842">
                      {getStatusText(invoice)}
                    </span>
                  </div>
                </div>

                <div className="space-y-2 mb-3" data-id="element-843">
                  <div className="flex items-center text-sm text-slate-600" data-id="element-844">
                    <DollarSign size={14} className="mr-2 text-slate-400" data-id="element-845" />
                    <span className="font-semibold" data-id="element-846">
                      ${invoice.total.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex items-center text-sm text-slate-600" data-id="element-847">
                    <Calendar size={14} className="mr-2 text-slate-400" data-id="element-848" />
                    <span className="text-xs text-slate-500" data-id="element-849">Invoice:</span>
                    <span className="ml-1" data-id="element-850">
                      {new Date(invoice.created).toLocaleDateString()}
                    </span>
                  </div>
                  {getRentalForInvoice(invoice)?.proposedEventDate && <div className="flex items-center text-sm text-slate-600" data-id="element-851">
                      <Calendar size={14} className="mr-2 text-slate-400" data-id="element-852" />
                      <span className="text-xs text-slate-500" data-id="element-853">Event:</span>
                      <span className="ml-1 font-medium" data-id="element-854">
                        {(() => {
                  const rental = getRentalForInvoice(invoice);
                  if (!rental) return 'N/A';
                  const startDate = new Date(rental.proposedEventDate + 'T00:00:00');
                  const startDateStr = startDate.toLocaleDateString('en-US', {
                    month: 'numeric',
                    day: 'numeric',
                    year: 'numeric'
                  });
                  if (rental.selections?.numberOfDays > 1) {
                    const endDate = new Date(startDate);
                    endDate.setDate(endDate.getDate() + rental.selections.numberOfDays - 1);
                    const endDateStr = endDate.toLocaleDateString('en-US', {
                      month: 'numeric',
                      day: 'numeric',
                      year: 'numeric'
                    });
                    return `${startDateStr} - ${endDateStr} (${rental.selections.numberOfDays} Days)`;
                  }
                  return startDateStr;
                })()}
                      </span>
                    </div>}

                  {(() => {
              const rental = getRentalForInvoice(invoice);
              const location = rental?.customerInfo?.eventVenueCityState || [rental?.customerInfo?.city, rental?.customerInfo?.state].filter(Boolean).join(', ');
              if (location) {
                return <div className="flex items-center text-sm text-slate-600" data-id="element-855">
                          <MapPin size={14} className="mr-2 text-slate-400" data-id="element-856" />
                          <span className="truncate" data-id="element-857">{location}</span>
                        </div>;
              }
              return null;
            })()}

                  {getRingCount(invoice) > 0 && <div className="flex items-center text-sm font-semibold text-blue-700" data-id="element-858">
                      <span className="mr-2" data-id="element-859">🔵</span>
                      {getRingCount(invoice)} Ring
                      {getRingCount(invoice) !== 1 ? 's' : ''}
                    </div>}
                  {getAssignedTechnician(invoice) && <div className="flex items-center text-sm text-slate-600" data-id="element-860">
                      <User size={14} className="mr-2 text-slate-400" data-id="element-861" />
                      <span className="font-medium" data-id="element-862">
                        {getAssignedTechnician(invoice)}
                      </span>
                    </div>}
                </div>

                {invoice.balance > 0 && <div className="pt-3 border-t border-slate-100 mb-3" data-id="element-863">
                    <div className="flex justify-between text-sm" data-id="element-864">
                      <span className="text-slate-600" data-id="element-865">Balance Due:</span>
                      <span className="font-semibold text-red-600" data-id="element-866">
                        ${invoice.balance.toFixed(2)}
                      </span>
                    </div>
                  </div>}
              </div>

              {/* Customer Link Section */}
              <div className="mb-3 p-3 bg-slate-50 rounded-lg border border-slate-200" data-id="element-867">
                <div className="text-xs text-slate-600 mb-2" data-id="element-868">
                  Customer Link:
                </div>
                <div className="flex items-center gap-2" data-id="element-869">
                  <button onClick={e => openInvoiceLink(invoice, e)} className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium" data-id="element-870">
                    <ExternalLink size={14} data-id="element-871" />
                    View Invoice
                  </button>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2" data-id="element-872">
                <button onClick={e => {
            e.stopPropagation();
            setSelectedInvoice(invoice);
            if (invoice.balance > 0) {
              startEditing(invoice);
            }
          }} className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors text-sm font-medium" data-id="element-873">
                  <Edit2 size={14} data-id="element-874" />
                  Edit
                </button>
                {invoice.balance > 0 && <button onClick={e => startApplyingPayment(invoice, e)} className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium" data-id="element-875">
                    <DollarSign size={14} data-id="element-876" />
                    Payment
                  </button>}
                <button onClick={e => copyLinkToClipboard(invoice, e)} className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium relative" data-id="element-877">
                  <Copy size={14} data-id="element-878" />
                  {copiedInvoiceId === invoice.id ? 'Copied!' : 'Copy Link'}
                </button>
              </div>
            </div>)}
        </div>}

      {/* Invoice Detail Modal */}
      {selectedInvoice && !editingInvoice && <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-id="element-879">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" data-id="element-880">
            <div className="sticky top-0 bg-white border-b border-slate-200 p-4 sm:p-6 flex items-center justify-between" data-id="element-881">
              <h3 className="text-lg sm:text-xl font-bold text-slate-800" data-id="element-882">
                Invoice Details
              </h3>
              <div className="flex items-center gap-1 sm:gap-2" data-id="element-883">
                <button onClick={() => copyLinkToClipboard(selectedInvoice)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors relative" title="Copy Payment Link" data-id="element-884">
                  <Copy size={18} data-id="element-885" />
                  {copiedInvoiceId === selectedInvoice.id && <span className="absolute top-full right-0 mt-1 bg-slate-800 text-white text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap z-10" data-id="element-886">
                      Link Copied!
                    </span>}
                </button>
                {selectedInvoice.balance > 0 && <button onClick={() => startEditing(selectedInvoice)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Edit Invoice" data-id="element-887">
                    <Edit2 size={18} data-id="element-888" />
                  </button>}
                {selectedInvoice.balance > 0 && <button onClick={() => startApplyingPayment(selectedInvoice)} className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors" title="Apply Payment" data-id="element-889">
                    <DollarSign size={18} data-id="element-890" />
                  </button>}
                <button onClick={e => handleDeleteClick(selectedInvoice.id, e)} disabled={selectedInvoice.balance === 0 && selectedInvoice.total > 0} className={`p-2 rounded-lg transition-colors ${selectedInvoice.balance === 0 && selectedInvoice.total > 0 ? 'text-slate-400 cursor-not-allowed' : selectedInvoice.total === 0 && (deleteClickCount[selectedInvoice.id] || 0) < 3 ? 'text-slate-400 cursor-pointer' : 'text-red-600 hover:bg-red-50'}`} title={selectedInvoice.balance === 0 && selectedInvoice.total > 0 ? 'Cannot delete paid invoice' : selectedInvoice.total === 0 ? `Triple-click to enable delete (${deleteClickCount[selectedInvoice.id] || 0}/3)` : 'Delete Invoice'} data-id="element-891">
                  <Trash2 size={18} data-id="element-892" />
                </button>
                <button onClick={() => setSelectedInvoice(null)} className="p-2 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-893">
                  <X size={20} className="text-slate-600" data-id="element-894" />
                </button>
              </div>
            </div>

            <div className="p-4 sm:p-6 space-y-6" data-id="element-895">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-id="element-896">
                <div data-id="element-897">
                  <h4 className="text-xl sm:text-2xl font-bold text-slate-800 break-words" data-id="element-898">
                    {selectedInvoice.invoiceNumber}
                  </h4>
                  <p className="text-sm text-slate-500" data-id="element-899">
                    Invoice Created:{' '}
                    {new Date(selectedInvoice.created).toLocaleDateString()}
                  </p>
                  {getRentalForInvoice(selectedInvoice)?.proposedEventDate && <p className="text-sm text-slate-700 font-medium" data-id="element-900">
                      Event Date:{' '}
                      {(() => {
                  const rental = getRentalForInvoice(selectedInvoice);
                  if (!rental) return 'N/A';
                  const startDate = new Date(rental.proposedEventDate + 'T00:00:00');
                  const startDateStr = startDate.toLocaleDateString('en-US', {
                    month: 'numeric',
                    day: 'numeric',
                    year: 'numeric'
                  });
                  if (rental.selections?.numberOfDays > 1) {
                    const endDate = new Date(startDate);
                    endDate.setDate(endDate.getDate() + rental.selections.numberOfDays - 1);
                    const endDateStr = endDate.toLocaleDateString('en-US', {
                      month: 'numeric',
                      day: 'numeric',
                      year: 'numeric'
                    });
                    return `${startDateStr} - ${endDateStr} (${rental.selections.numberOfDays} Days)`;
                  }
                  return startDateStr;
                })()}
                    </p>}
                </div>
                <span className={`px-3 py-1 rounded-full text-sm font-medium border self-start sm:self-auto ${getStatusColor(selectedInvoice)}`} data-id="element-901">
                  {getStatusText(selectedInvoice)}
                </span>
              </div>

              {selectedInvoice.balance === 0 && <div className="bg-red-50 border border-red-200 rounded-lg p-3" data-id="element-902">
                  <div className="flex items-start text-red-800" data-id="element-903">
                    <AlertCircle size={16} className="mr-2 flex-shrink-0 mt-0.5" data-id="element-904" />
                    <span className="text-sm font-medium" data-id="element-905">
                      This invoice is fully paid and cannot be edited
                    </span>
                  </div>
                </div>}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-906">
                <div data-id="element-907">
                  <label className="text-sm font-medium text-slate-500" data-id="element-908">
                    Customer
                  </label>
                  <p className="mt-1 text-slate-800 font-medium break-words" data-id="element-909">
                    {selectedInvoice.customerName}
                  </p>
                  <p className="text-sm text-slate-600 break-words" data-id="element-910">
                    {selectedInvoice.customerEmail}
                  </p>
                  {(() => {
                const rental = getRentalForInvoice(selectedInvoice);
                const hasAddress = rental?.customerInfo?.address || rental?.customerInfo?.city;
                if (hasAddress) {
                  return <div className="mt-2 text-sm text-slate-600 space-y-0.5" data-id="element-911">
                          {rental.customerInfo?.address && <p data-id="element-912">{rental.customerInfo.address}</p>}
                          {rental.customerInfo?.address2 && <p data-id="element-913">{rental.customerInfo.address2}</p>}
                          {(rental.customerInfo?.city || rental.customerInfo?.state || rental.customerInfo?.zip) && <p data-id="element-914">
                              {[rental.customerInfo?.city, rental.customerInfo?.state, rental.customerInfo?.zip].filter(Boolean).join(', ')}
                            </p>}
                        </div>;
                }
                return null;
              })()}
                </div>
                {getAssignedTechnician(selectedInvoice) && <div data-id="element-915">
                    <label className="text-sm font-medium text-slate-500" data-id="element-916">
                      Assigned Technician
                    </label>
                    <p className="mt-1 text-slate-800 font-medium break-words" data-id="element-917">
                      {getAssignedTechnician(selectedInvoice)}
                    </p>
                  </div>}
              </div>

              <div data-id="element-918">
                <label className="text-sm font-medium text-slate-500 mb-2 block" data-id="element-919">
                  Line Items
                </label>
                <div className="bg-slate-50 rounded-lg overflow-hidden overflow-x-auto" data-id="element-920">
                  <table className="w-full min-w-[500px]" data-id="element-921">
                    <thead className="bg-slate-100" data-id="element-922">
                      <tr data-id="element-923">
                        <th className="text-left p-3 text-sm font-medium text-slate-700" data-id="element-924">
                          Description
                        </th>
                        <th className="text-right p-3 text-sm font-medium text-slate-700" data-id="element-925">
                          Qty
                        </th>
                        <th className="text-right p-3 text-sm font-medium text-slate-700" data-id="element-926">
                          Price
                        </th>
                        <th className="text-right p-3 text-sm font-medium text-slate-700" data-id="element-927">
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody data-id="element-928">
                      {selectedInvoice.lineItems.map(item => <tr key={item.id} className="border-t border-slate-200" data-id="element-929">
                          <td className="p-3 text-sm text-slate-800" data-id="element-930">
                            {item.description}
                          </td>
                          <td className="p-3 text-sm text-slate-800 text-right" data-id="element-931">
                            {item.quantity}
                          </td>
                          <td className="p-3 text-sm text-slate-800 text-right" data-id="element-932">
                            ${item.price.toFixed(2)}
                          </td>
                          <td className="p-3 text-sm font-medium text-slate-800 text-right" data-id="element-933">
                            ${item.total.toFixed(2)}
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="space-y-2" data-id="element-934">
                <div className="flex justify-between" data-id="element-935">
                  <span className="text-slate-600" data-id="element-936">Subtotal</span>
                  <span className="font-medium" data-id="element-937">
                    ${selectedInvoice.subtotal.toFixed(2)}
                  </span>
                </div>
                {selectedInvoice.discount > 0 && <div className="flex justify-between text-green-700" data-id="element-938">
                    <span data-id="element-939">Discount</span>
                    <span className="font-medium" data-id="element-940">
                      -${selectedInvoice.discount.toFixed(2)}
                    </span>
                  </div>}
                {selectedInvoice.tax > 0 && <div className="flex justify-between text-red-700" data-id="element-941">
                    <span data-id="element-942">Tax</span>
                    <span className="font-medium" data-id="element-943">
                      ${selectedInvoice.tax.toFixed(2)}
                    </span>
                  </div>}
                <div className="flex justify-between text-lg font-bold pt-2 border-t border-slate-200" data-id="element-944">
                  <span data-id="element-945">Total</span>
                  <span data-id="element-946">${selectedInvoice.total.toFixed(2)}</span>
                </div>
                {selectedInvoice.amountPaid > 0 && <>
                    <div className="flex justify-between text-green-700" data-id="element-947">
                      <span data-id="element-948">Amount Paid</span>
                      <span className="font-medium" data-id="element-949">
                        ${selectedInvoice.amountPaid.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-lg font-bold text-red-700" data-id="element-950">
                      <span data-id="element-951">Balance Due</span>
                      <span data-id="element-952">${selectedInvoice.balance.toFixed(2)}</span>
                    </div>
                  </>}
              </div>
            </div>
          </div>
        </div>}

      {/* Edit Invoice Modal */}
      {editingInvoice && <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-id="element-953">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto" data-id="element-954">
            <div className="sticky top-0 bg-white border-b border-slate-200 p-4 sm:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-id="element-955">
              <h3 className="text-lg sm:text-xl font-bold text-slate-800 break-words" data-id="element-956">
                Edit Invoice - {editingInvoice.invoiceNumber}
              </h3>
              <div className="flex items-center gap-2" data-id="element-957">
                <button onClick={saveEdit} className="flex-1 sm:flex-none px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2 text-sm sm:text-base" data-id="element-958">
                  <Check size={18} data-id="element-959" />
                  Save
                </button>
                <button onClick={cancelEdit} className="flex-1 sm:flex-none px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors text-sm sm:text-base" data-id="element-960">
                  Cancel
                </button>
              </div>
            </div>

            <div className="p-4 sm:p-6 space-y-6" data-id="element-961">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-962">
                <div data-id="element-963">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-964">
                    Customer Name
                  </label>
                  <input type="text" value={editingInvoice.customerName} onChange={e => updateInvoiceField('customerName', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-965" />
                </div>
                <div data-id="element-966">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-967">
                    Customer Email
                  </label>
                  <input type="email" value={editingInvoice.customerEmail} onChange={e => updateInvoiceField('customerEmail', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-968" />
                </div>
              </div>

              {/* Shipping Address Section */}
              <div className="border-t border-slate-200 pt-6" data-id="element-969">
                <h4 className="text-md font-semibold text-slate-800 mb-4" data-id="element-970">
                  Shipping Address
                </h4>
                <div className="space-y-4" data-id="element-971">
                  <div data-id="element-972">
                    <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-973">
                      Address Line 1
                    </label>
                    <input type="text" value={editingInvoice.address || ''} onChange={e => updateInvoiceField('address', e.target.value)} placeholder="Street address" className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-974" />
                  </div>
                  <div data-id="element-975">
                    <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-976">
                      Address Line 2
                    </label>
                    <input type="text" value={editingInvoice.address2 || ''} onChange={e => updateInvoiceField('address2', e.target.value)} placeholder="Apartment, suite, etc. (optional)" className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-977" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-978">
                    <div data-id="element-979">
                      <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-980">
                        City
                      </label>
                      <input type="text" value={editingInvoice.city || ''} onChange={e => updateInvoiceField('city', e.target.value)} placeholder="City" className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-981" />
                    </div>
                    <div data-id="element-982">
                      <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-983">
                        State
                      </label>
                      <input type="text" value={editingInvoice.state || ''} onChange={e => updateInvoiceField('state', e.target.value)} placeholder="State" className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-984" />
                    </div>
                    <div data-id="element-985">
                      <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-986">
                        ZIP Code
                      </label>
                      <input type="text" value={editingInvoice.zip || ''} onChange={e => updateInvoiceField('zip', e.target.value)} placeholder="ZIP" className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-987" />
                    </div>
                  </div>
                </div>
              </div>

              <div data-id="element-988">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2" data-id="element-989">
                  <label className="text-sm font-medium text-slate-700" data-id="element-990">
                    Line Items
                  </label>
                  <button onClick={addLineItem} className="self-start sm:self-auto px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors flex items-center gap-1" data-id="element-991">
                    <Plus size={14} data-id="element-992" />
                    Add Item
                  </button>
                </div>
                <div className="bg-slate-50 rounded-lg overflow-hidden overflow-x-auto" data-id="element-993">
                  <table className="w-full min-w-[600px]" data-id="element-994">
                    <thead className="bg-slate-100" data-id="element-995">
                      <tr data-id="element-996">
                        <th className="text-left p-3 text-sm font-medium text-slate-700" data-id="element-997">
                          Description
                        </th>
                        <th className="text-right p-3 text-sm font-medium text-slate-700" data-id="element-998">
                          Qty
                        </th>
                        <th className="text-right p-3 text-sm font-medium text-slate-700" data-id="element-999">
                          Price
                        </th>
                        <th className="text-right p-3 text-sm font-medium text-slate-700" data-id="element-1000">
                          Total
                        </th>
                        <th className="w-10" data-id="element-1001"></th>
                      </tr>
                    </thead>
                    <tbody data-id="element-1002">
                      {editingInvoice.lineItems.map(item => <tr key={item.id} className="border-t border-slate-200" data-id="element-1003">
                          <td className="p-2" data-id="element-1004">
                            <input type="text" value={item.description} onChange={e => updateLineItem(item.id, 'description', e.target.value)} className="w-full px-2 py-1 text-sm border border-slate-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1005" />
                          </td>
                          <td className="p-2" data-id="element-1006">
                            <input type="number" value={item.quantity} onChange={e => updateLineItem(item.id, 'quantity', parseInt(e.target.value) || 0)} className="w-full px-2 py-1 text-sm border border-slate-300 rounded text-right focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1007" />
                          </td>
                          <td className="p-2" data-id="element-1008">
                            <input type="number" value={item.price} onChange={e => updateLineItem(item.id, 'price', parseFloat(e.target.value) || 0)} step="1" className="w-full px-2 py-1 text-sm border border-slate-300 rounded text-right focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1009" />
                          </td>
                          <td className="p-2 text-sm font-medium text-slate-800 text-right" data-id="element-1010">
                            ${item.total.toFixed(2)}
                          </td>
                          <td className="p-2" data-id="element-1011">
                            <button onClick={() => removeLineItem(item.id)} className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors" data-id="element-1012">
                              <Trash2 size={14} data-id="element-1013" />
                            </button>
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4" data-id="element-1014">
                <div data-id="element-1015">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1016">
                    Discount
                  </label>
                  <input type="number" value={editingInvoice.discount} onChange={e => updateInvoiceField('discount', parseFloat(e.target.value) || 0)} step="1" className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1017" />
                </div>
                <div data-id="element-1018">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1019">
                    Tax
                  </label>
                  <input type="number" value={editingInvoice.tax} onChange={e => updateInvoiceField('tax', parseFloat(e.target.value) || 0)} step="1" className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1020" />
                </div>
                <div data-id="element-1021">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1022">
                    Amount Paid
                  </label>
                  <input type="number" value={editingInvoice.amountPaid} onChange={e => updateInvoiceField('amountPaid', parseFloat(e.target.value) || 0)} step="1" className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1023" />
                </div>
              </div>

              <div className="bg-blue-50 rounded-lg p-4 space-y-2" data-id="element-1024">
                <div className="flex justify-between" data-id="element-1025">
                  <span className="text-slate-700" data-id="element-1026">Subtotal</span>
                  <span className="font-medium" data-id="element-1027">
                    ${editingInvoice.subtotal.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between text-lg font-bold" data-id="element-1028">
                  <span data-id="element-1029">Total</span>
                  <span data-id="element-1030">${editingInvoice.total.toFixed(2)}</span>
                </div>
                {editingInvoice.amountPaid > 0 && <div className="flex justify-between text-lg font-bold text-red-700 pt-2 border-t border-blue-200" data-id="element-1031">
                    <span data-id="element-1032">Balance Due</span>
                    <span data-id="element-1033">${editingInvoice.balance.toFixed(2)}</span>
                  </div>}
              </div>
            </div>
          </div>
        </div>}

      {/* Apply Payment Modal */}
      {applyingPayment && <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-id="element-1034">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-md" data-id="element-1035">
            <div className="bg-white border-b border-slate-200 p-4 sm:p-6 flex items-center justify-between" data-id="element-1036">
              <h3 className="text-lg sm:text-xl font-bold text-slate-800" data-id="element-1037">
                Apply Payment
              </h3>
              <button onClick={cancelApplyingPayment} className="p-2 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-1038">
                <X size={20} className="text-slate-600" data-id="element-1039" />
              </button>
            </div>

            <div className="p-4 sm:p-6 space-y-4" data-id="element-1040">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-1041">
                <div className="space-y-2" data-id="element-1042">
                  <div className="flex justify-between text-sm" data-id="element-1043">
                    <span className="text-slate-600" data-id="element-1044">Invoice:</span>
                    <span className="font-semibold text-slate-800" data-id="element-1045">
                      {applyingPayment.invoiceNumber}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm" data-id="element-1046">
                    <span className="text-slate-600" data-id="element-1047">Customer:</span>
                    <span className="font-semibold text-slate-800" data-id="element-1048">
                      {applyingPayment.customerName}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm" data-id="element-1049">
                    <span className="text-slate-600" data-id="element-1050">Total:</span>
                    <span className="font-semibold text-slate-800" data-id="element-1051">
                      ${applyingPayment.total.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm" data-id="element-1052">
                    <span className="text-slate-600" data-id="element-1053">Already Paid:</span>
                    <span className="font-semibold text-green-700" data-id="element-1054">
                      ${applyingPayment.amountPaid.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between text-lg font-bold pt-2 border-t border-blue-300" data-id="element-1055">
                    <span className="text-slate-800" data-id="element-1056">Balance Due:</span>
                    <span className="text-red-700" data-id="element-1057">
                      ${applyingPayment.balance.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              <div data-id="element-1058">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1059">
                  Payment Amount <span className="text-red-500" data-id="element-1060">*</span>
                </label>
                <div className="relative" data-id="element-1061">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" data-id="element-1062">
                    $
                  </span>
                  <input type="number" value={paymentAmount} onChange={e => setPaymentAmount(e.target.value)} step="0.01" min="0.01" max={applyingPayment.balance} className="w-full pl-8 pr-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500" placeholder="0.00" data-id="element-1063" />
                </div>
              </div>

              <div data-id="element-1064">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1065">
                  Payment Method <span className="text-red-500" data-id="element-1066">*</span>
                </label>
                <select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500" data-id="element-1067">
                  <option value="cash" data-id="element-1068">Cash</option>
                  <option value="check" data-id="element-1069">Check</option>
                </select>
              </div>

              <div data-id="element-1070">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1071">
                  Notes (Optional)
                </label>
                <textarea value={paymentNotes} onChange={e => setPaymentNotes(e.target.value)} rows={3} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none" placeholder="Check number, receipt details, etc." data-id="element-1072" />
              </div>

              <div className="flex gap-3 pt-4" data-id="element-1073">
                <button onClick={cancelApplyingPayment} disabled={isProcessingPayment} className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1074">
                  Cancel
                </button>
                <button onClick={submitPayment} disabled={!paymentAmount || parseFloat(paymentAmount) <= 0 || isProcessingPayment} className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium disabled:bg-slate-300 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-1075">
                  {isProcessingPayment ? <>
                      <Loader2 size={18} className="animate-spin" data-id="element-1076" />
                      Processing...
                    </> : <>
                      <Check size={18} data-id="element-1077" />
                      Apply Payment
                    </>}
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Payment Success Modal */}
      {showPaymentSuccess && successPaymentDetails && <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4" data-id="element-1078">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-md overflow-hidden" data-id="element-1079">
            {/* Success Header with Gradient */}
            <div className="bg-gradient-to-br from-green-500 to-emerald-600 p-6 text-center" data-id="element-1080">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-full mb-4" data-id="element-1081">
                <CheckCircle2 className="text-green-600" size={32} data-id="element-1082" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2" data-id="element-1083">
                Payment Applied Successfully!
              </h3>
              <p className="text-green-50 text-sm" data-id="element-1084">
                The payment has been recorded
              </p>
            </div>

            {/* Success Body */}
            <div className="p-6" data-id="element-1085">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4" data-id="element-1086">
                <div className="space-y-2 text-sm" data-id="element-1087">
                  <div className="flex justify-between" data-id="element-1088">
                    <span className="text-green-700 font-medium" data-id="element-1089">Invoice:</span>
                    <span className="text-green-900 font-semibold" data-id="element-1090">
                      {successPaymentDetails.invoiceNumber}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-1091">
                    <span className="text-green-700 font-medium" data-id="element-1092">
                      Customer:
                    </span>
                    <span className="text-green-900" data-id="element-1093">
                      {successPaymentDetails.customerName}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-1094">
                    <span className="text-green-700 font-medium" data-id="element-1095">
                      Payment Amount:
                    </span>
                    <span className="text-green-900 font-bold text-lg" data-id="element-1096">
                      ${successPaymentDetails.amount.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-1097">
                    <span className="text-green-700 font-medium" data-id="element-1098">Method:</span>
                    <span className="text-green-900 capitalize" data-id="element-1099">
                      {successPaymentDetails.method}
                    </span>
                  </div>
                  {successPaymentDetails.notes && <div className="pt-2 border-t border-green-200" data-id="element-1100">
                      <span className="text-green-700 font-medium block mb-1" data-id="element-1101">
                        Notes:
                      </span>
                      <span className="text-green-900 text-xs" data-id="element-1102">
                        {successPaymentDetails.notes}
                      </span>
                    </div>}
                </div>
              </div>

              {successPaymentDetails.newBalance > 0 ? <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4" data-id="element-1103">
                  <div className="flex items-start gap-2" data-id="element-1104">
                    <AlertCircle size={16} className="text-yellow-600 flex-shrink-0 mt-0.5" data-id="element-1105" />
                    <div className="text-sm" data-id="element-1106">
                      <p className="font-medium text-yellow-800 mb-1" data-id="element-1107">
                        Remaining Balance
                      </p>
                      <p className="text-yellow-700" data-id="element-1108">
                        ${successPaymentDetails.newBalance.toFixed(2)} still due
                        on this invoice
                      </p>
                    </div>
                  </div>
                </div> : <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4" data-id="element-1109">
                  <div className="flex items-start gap-2" data-id="element-1110">
                    <CheckCircle2 size={16} className="text-green-600 flex-shrink-0 mt-0.5" data-id="element-1111" />
                    <div className="text-sm" data-id="element-1112">
                      <p className="font-medium text-green-800" data-id="element-1113">
                        Invoice Paid in Full
                      </p>
                      <p className="text-green-700 text-xs mt-1" data-id="element-1114">
                        This invoice has been completely paid off
                      </p>
                    </div>
                  </div>
                </div>}

              <button onClick={() => {
            setShowPaymentSuccess(false);
            setSuccessPaymentDetails(null);
          }} className="w-full px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold" data-id="element-1115">
                Done
              </button>
            </div>
          </div>
        </div>}
    </div>;
}